/**
 * Wave 29 Chunk 8c — Shared media lightbox.
 *
 * Bug fix (Dirk, 2026-05-11): "gellery, cusotmer gally, job card photos etc
 * do not allow swipe trhourg or button tap to next or previous like a real
 * phone gallery."
 *
 * Iter 347 (2026-02-26) — Dirk: "Customer portal photo view in job card
 * (also chrck support and gallery) does not have a close button, download
 * button and next/back. Make it thr same as crm side job card photo view".
 * Two things fixed here:
 *   (1) Added a first-class **Download** button — fetches the media via
 *       the axios interceptor (so auth/impersonation cookies ride along)
 *       and saves as a Blob. Works for R2 signed URLs, /api/uploads/{id}
 *       and data:URLs. Never opens a new tab (would break iOS PWAs).
 *   (2) Boosted the top-bar contrast from `bg-white/10` to `bg-black/60`
 *       so the controls are clearly visible against bright photos on
 *       mobile. The old translucent buttons vanished against overexposed
 *       JC shots taken outdoors.
 *
 * Drop-in viewer for any list of images / videos that gives users:
 *  - Close button (top-left)
 *  - Prev / Next ChevronLeft/Right buttons (44px tap targets)
 *  - Touch swipe (left/right) with a 40px threshold
 *  - Keyboard arrow-key navigation + Esc close
 *  - Counter `i / n` (only when more than one)
 *  - Download + Share buttons (top-right)
 *  - Auto-loops at start/end so users never get stuck
 *
 * Renders nothing when `index < 0` (so consumers can do
 * `<MediaLightbox items={photos} index={open} ... />` and just set index = -1
 * to close).
 */
import { useEffect, useRef, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X, Share2, Download, Loader2 } from "lucide-react";
import { useShareable } from "../hooks/useShareable";
import { shareFile } from "../lib/shareFile";
import api from "../lib/api";
import { toast } from "./ui/sonner";

export default function MediaLightbox({
  items,           // [{ src, kind?: "image"|"video", caption?, filename? }, ...]
  index,           // current index, -1 / null when closed
  onChange,        // (newIndex: number) => void
  onClose,         // () => void
  topBar = null,   // optional custom node rendered at top-right (extra actions)
  bottomBar = null,// optional custom node below the image
}) {
  const touchStartX = useRef(null);
  const open = index != null && index >= 0 && index < (items || []).length;
  const current = open ? items[index] : null;
  const total = items?.length || 0;
  const hasNav = total > 1;
  const [downloading, setDownloading] = useState(false);

  const goPrev = () => onChange(((index - 1) + total) % total);
  const goNext = () => onChange((index + 1) % total);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft" && hasNav) goPrev();
      else if (e.key === "ArrowRight" && hasNav) goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, index, total, hasNav]);

  // Iter 187 — universal share on the lightbox (long-press / right-click).
  // Hooks must run unconditionally — keep ABOVE the early return.
  const shareDesc = useMemo(() => current?.src ? ({
    url: current.src,
    filename: current.filename || current.file_name || current.caption || null,
    contentType: current.contentType || current.content_type || null,
    title: current.caption || current.title || current.filename || "media",
  }) : null, [current]);
  const { handlers: shareHandlers, menu: shareMenu } = useShareable(shareDesc);

  if (!open) return null;

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null || !hasNav) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx > 0 ? goPrev() : goNext());
    touchStartX.current = null;
  };

  const isVideo = current.kind === "video" || /\.(mp4|mov|webm)(\?|$)/i.test(current.src || "");

  // [Iter 347] Auth-carrying blob download. Matches the pattern used by
  // `PortalJobCardDetail.downloadPortalDoc` — hits the same axios
  // instance so auth headers, impersonation, and tenant scoping are all
  // preserved. Never opens a new tab (would break iOS PWA context).
  const doDownload = async (e) => {
    e?.stopPropagation?.();
    if (!current?.src || downloading) return;
    setDownloading(true);
    try {
      const src = current.src;
      const defaultName = current.filename
        || current.file_name
        || current.caption
        || `media_${(index || 0) + 1}`;
      let blob;
      if (src.startsWith("data:")) {
        const resp = await fetch(src);
        blob = await resp.blob();
      } else if (src.startsWith("http")) {
        // Absolute URL — could be an R2 signed URL or an external asset.
        // Fetch with credentials in case it's a same-origin authed path.
        const resp = await fetch(src, { credentials: "include" });
        if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
        blob = await resp.blob();
      } else {
        // Bare path like /api/uploads/{fid} — route via api so the
        // interceptor injects Bearer + impersonation cookie.
        const path = src.startsWith("/api/") ? src.slice(4) : src;
        const resp = await api.get(path, { responseType: "blob" });
        blob = resp.data;
      }
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = defaultName;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 15_000);
    } catch (err) {
      toast.error(err?.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  // Shared button styling — bg-black/60 for opacity so the controls
  // stay legible against any photo underneath (including bright ones).
  const btnCls = "hv-btn text-xs bg-black/60 hover:bg-black/80 text-white border-white/30 backdrop-blur-sm";

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur flex flex-col hv-safe-top hv-safe-bottom hv-safe-x"
      onClick={onClose}
      data-testid="media-lightbox"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex items-center justify-between px-3 py-2 gap-2 border-b border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className={btnCls}
          data-testid="media-lightbox-close"
          type="button"
          aria-label="Close"
        >
          <X className="w-4 h-4" /> Close
        </button>
        {hasNav && (
          <span
            className="text-[11px] text-white/80 tabular-nums font-mono"
            data-testid="media-lightbox-counter"
          >
            {index + 1} / {total}
          </span>
        )}
        <div className="flex items-center gap-2 min-w-[60px] justify-end">
          {shareDesc && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                shareFile(shareDesc);
              }}
              className={btnCls}
              data-testid="media-lightbox-share"
              aria-label="Share"
              title="Share file"
            >
              <Share2 className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={doDownload}
            disabled={downloading}
            className={`${btnCls} disabled:opacity-50`}
            data-testid="media-lightbox-download"
            aria-label="Download"
            title="Download file"
          >
            {downloading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Download className="w-4 h-4" />}
          </button>
          {topBar}
        </div>
      </div>

      <div
        className="flex-1 flex items-center justify-center min-h-0 relative select-none"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {hasNav && (
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 border border-white/30 backdrop-blur-sm flex items-center justify-center text-white z-10"
            aria-label="Previous"
            data-testid="media-lightbox-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {isVideo ? (
          <video
            key={current.src}
            src={current.src}
            controls
            autoPlay={false}
            className="max-w-full max-h-full"
            data-testid="media-lightbox-video"
          />
        ) : (
          <img
            key={current.src}
            src={current.src}
            alt={current.caption || ""}
            className="max-w-full max-h-full object-contain"
            data-testid="media-lightbox-image"
            draggable={false}
            {...shareHandlers}
          />
        )}
        {hasNav && (
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 border border-white/30 backdrop-blur-sm flex items-center justify-center text-white z-10"
            aria-label="Next"
            data-testid="media-lightbox-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>

      {shareMenu}
      {bottomBar && (
        <div onClick={(e) => e.stopPropagation()}>
          {bottomBar}
        </div>
      )}
    </div>
  );
}
